/* eslint-disable */
// src/hooks/useVisitBootstrap.js

import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import VisitTransactionService from "services/visitTransactionService";
import { normalizeCollectionPreviousValueRows } from "services/visitMachineService";
import LocalMachineService from "services/localMachineService";
import LocalMachineProfitPercentageService from "services/localMachineProfitPercentageService";
import {
  isCompletedEditDetailResponseForVisit,
  mergeCollectionLocalSources,
  normalizeCompletedEditDetailMachine,
  normalizeVisitDetailLocal,
} from "layouts/visitMachine/collectionWorkflow";

/* 
 * Hook para MODO EDICIÓN cuando no llega todo por navigation state.
 * Trae detalle de la visita, LM y porcentajes según el local efectivo.
 * Mapea las máquinas a shape histórico de edición y arma lastCredits.
 * Expone loading/error y los datos listos para hidratar VisitMachine.
 */
export default function useVisitBootstrap() {
  const { id: localIdParam, visitId } = useParams();     // ✅ id = localId, visitId = recaudación
  const { state } = useLocation();
  const isEdit = Boolean(visitId);                       // ✅ solo edita si existe visitId

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const localFromState = normalizeVisitDetailLocal({ local: state?.local || null });
  const localIdFromState = state?.local?.Id || state?.localId || null;

  const [local, setLocal] = useState(localFromState);
  const [profitPercentages, setProfitPercentages] = useState(state?.profitPercentages || []);
  const [localMachineDetails, setLocalMachineDetails] = useState(state?.localMachineDetails || []);
  const [calculatedVisitMachines, setCalculatedVisitMachines] = useState(
    state?.calculatedVisitMachines?.map(normalizeCompletedEditDetailMachine) || []
  );
  const [lastCredits, setLastCredits] = useState(
    normalizeCollectionPreviousValueRows(state?.lastCredits || [])
  );
  const [visitWithCollectionId, setVisitWithCollectionId] = useState(
    state?.visitWithCollectionId || visitId || null
  );
  const [loadedVisitId, setLoadedVisitId] = useState(null);
  const [visitWithCollection, setVisitWithCollection] = useState(
    state?.visitWithCollection || state?.VisitWithCollection || null
  );
  const [visitDateTime, setVisitDateTime] = useState(state?.visitDateTime || null);
  const [lastLocalVisitBeforeId, setLastLocalVisitBeforeId] = useState(
    state?.LastLocalVisitBeforeId ?? state?.lastLocalVisitBeforeId ?? null
  );
  const [lastLocalVisitBeforeDate, setLastLocalVisitBeforeDate] = useState(
    state?.LastLocalVisitBeforeDate ?? state?.lastLocalVisitBeforeDate ?? null
  );

  const buildHistoricalLocalMachineRows = (currentLocalMachineRows = [], detailMachines = []) => {
    const currentRows = Array.isArray(currentLocalMachineRows) ? currentLocalMachineRows : [];
    const historicalRows = Array.isArray(detailMachines) ? detailMachines : [];
    const currentById = new Map(
      currentRows.map((row) => [Number(row?.LocalMachineId ?? row?.Id), row])
    );

    return historicalRows.map((row) => {
      const localMachineId = Number(row?.LocalMachineId ?? row?.Id ?? 0);
      const currentRow = currentById.get(localMachineId) || {};

      return {
        ...currentRow,
        ...row,
        Id: localMachineId,
        LocalMachineId: localMachineId,
        MachineType:
          currentRow?.MachineType ||
          (row?.MachineType || row?.MachineTypeId
            ? { Type: row?.MachineType ?? null, Id: row?.MachineTypeId ?? null }
            : null),
        MachineTypeId: row?.MachineTypeId ?? currentRow?.MachineTypeId ?? currentRow?.MachineType?.Id ?? null,
        Machine:
          (row?.MachineName || row?.SerialNumber || row?.LocalDisplayNumber != null
            ? {
                ...(currentRow?.Machine || {}),
                Name: row?.MachineName ?? currentRow?.Machine?.Name ?? null,
                SerialNumber: row?.SerialNumber ?? currentRow?.Machine?.SerialNumber ?? null,
                LocalDisplayNumber: row?.LocalDisplayNumber ?? currentRow?.LocalDisplayNumber ?? null,
              }
            : currentRow?.Machine) ||
          ((row?.MachineName || row?.SerialNumber)
            ? {
                Name: row?.MachineName ?? null,
                SerialNumber: row?.SerialNumber ?? null,
                LocalDisplayNumber: row?.LocalDisplayNumber ?? null,
              }
            : null),
        MachineName: row?.MachineName ?? currentRow?.MachineName ?? currentRow?.Machine?.Name ?? null,
        SerialNumber:
          row?.SerialNumber ?? currentRow?.SerialNumber ?? currentRow?.Machine?.SerialNumber ?? null,
        LocalDisplayNumber: row?.LocalDisplayNumber ?? currentRow?.LocalDisplayNumber ?? null,
        Formula:
          currentRow?.Formula ||
          (row?.FormulaId || row?.FormulaName || row?.FormulaType
            ? {
                Id: row?.FormulaId ?? null,
                Name: row?.FormulaName ?? null,
                Type: row?.FormulaType ?? null,
                Value: row?.FormulaValue ?? null,
              }
            : null),
        FormulaId: row?.FormulaId ?? currentRow?.FormulaId ?? currentRow?.Formula?.Id ?? null,
        FormulaName: row?.FormulaName ?? currentRow?.FormulaName ?? currentRow?.Formula?.Name ?? null,
        FormulaType: row?.FormulaType ?? currentRow?.FormulaType ?? currentRow?.Formula?.Type ?? null,
        FormulaValue: row?.FormulaValue ?? currentRow?.FormulaValue ?? currentRow?.Formula?.Value ?? null,
        IsAccumulative:
          typeof row?.IsAccumulative === "boolean"
            ? row.IsAccumulative
            : Boolean(currentRow?.IsAccumulative),
        IsActive:
          typeof currentRow?.IsActive === "boolean"
            ? currentRow.IsActive
            : false,
      };
    });
  };

  useEffect(() => {
    if (!isEdit) return; // ✅ en modo crear NO hace /details

    let alive = true;
    const requestedVisitId = Number(visitId);
    setLoading(true);
    setError(null);
    setLoadedVisitId(null);
    setCalculatedVisitMachines([]);
    setLocalMachineDetails([]);
    setLastCredits([]);
    setVisitWithCollection(null);
    setVisitDateTime(null);
    setLastLocalVisitBeforeId(null);
    setLastLocalVisitBeforeDate(null);
    (async () => {
      try {
        const effectiveLocalId = localIdFromState || localIdParam || null;

        const detail = await VisitTransactionService.getVisitTransactionDetails(
          String(visitId),
          { localId: effectiveLocalId ? String(effectiveLocalId) : null }
        );
        if (!alive) return;

        if (!detail) {
          setError("Recaudación no encontrada.");
          return;
        }

        if (!isCompletedEditDetailResponseForVisit(detail, requestedVisitId)) {
          throw new Error("La respuesta no corresponde a la visita solicitada.");
        }

        const finalLocalId = detail?.visit?.LocalId || effectiveLocalId || null;
        if (!finalLocalId) throw new Error("No se pudo determinar el LocalId.");

        const [lmDetails, profits] = await Promise.all([
          LocalMachineService.getLocalMachineByLocal(finalLocalId),
          LocalMachineProfitPercentageService.getLocalMachineProfitPercentagesByLocal(finalLocalId),
        ]);
        if (!alive) return;

        const normalizedBootLocal = normalizeVisitDetailLocal({
          local: detail?.local || null,
          person: detail?.person,
        }) || { Id: finalLocalId };
        setLocal((prev) => mergeCollectionLocalSources({
          stateLocal: state?.local || prev,
          bootLocal: normalizedBootLocal,
          expectedLocalId: finalLocalId,
        }) || normalizedBootLocal);
        setProfitPercentages((profits || []).filter((pp) => pp.IsActive !== false));

        const accumByLmId = new Map(
          (lmDetails || []).map((d) => [Number(d.Id || d.LocalMachineId), Boolean(d.IsAccumulative)])
        );

        const mapped = (detail?.machines || []).map(normalizeCompletedEditDetailMachine).map((m) => ({
          ...m,
          IsAccumulative:
            typeof m.IsAccumulative === "boolean"
              ? m.IsAccumulative
              : (accumByLmId.get(Number(m.LocalMachineId)) ?? false),
        }));

        const historicalLocalMachineRows = buildHistoricalLocalMachineRows(
          lmDetails || [],
          detail?.machines || []
        );

        setLocalMachineDetails(historicalLocalMachineRows);
        setCalculatedVisitMachines(mapped);

        const prev = normalizeCollectionPreviousValueRows(detail?.machines || []);
        setLastCredits(prev);

        const loadedId = detail?.visit?.Id || visitId;
        setVisitWithCollectionId(loadedId);
        setVisitWithCollection(detail?.visitWithCollection || detail?.VisitWithCollection || null);
        setVisitDateTime(detail?.visit?.DateTime || null);
        setLastLocalVisitBeforeId(detail?.LastLocalVisitBeforeId ?? null);
        setLastLocalVisitBeforeDate(detail?.LastLocalVisitBeforeDate ?? null);
        setLoadedVisitId(loadedId);
        setError(null);
      } catch (e) {
        if (!alive) return;
        if (process.env.NODE_ENV !== "production") console.error(e);
        setError("No se pudo cargar la recaudación para editar.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isEdit, visitId, localIdFromState, localIdParam]);

  return {
    isEdit,
    loading,
    error,
    local,
    profitPercentages,
    localMachineDetails,
    calculatedVisitMachines,
    lastCredits,
    visitWithCollectionId,
    loadedVisitId,
    visitWithCollection,
    visitDateTime,
    lastLocalVisitBeforeId,
    lastLocalVisitBeforeDate,
  };
}
